import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GearSix, SignOut,
  House, List, CaretLeft, DotsThree, Bell, ChatCircle,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/BrandLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { NotificationCenter } from '@/components/NotificationCenter';
import { FeedbackModal } from '@/components/FeedbackModal';
import { useState, useCallback, type MouseEvent } from 'react';
import Tippy, { useSingleton } from '@tippyjs/react';
import { animateFill } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/backdrop.css';
import 'tippy.js/animations/shift-away.css';
import { useAuth } from '@/contexts/AuthContext';
import { useGuestConversion } from '@/contexts/GuestConversionContext';

interface NavItem {
  label: string;
  href: string;
  icon: typeof House;
  /** Requer conta para acessar */
  guest_restricted?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Inicio', href: '/dashboard', icon: House },
  { label: 'Config.', href: '/settings', icon: GearSix },
];

const SIDEBAR_EXPANDED = 216;
const SIDEBAR_COLLAPSED = 72;

/** Painel flutuante com cores herdadas do tema via variáveis CSS */
const FLOATING_PANEL = cn(
  'bg-[hsl(var(--card)/0.95)] backdrop-blur-xl',
  'rounded-3xl shadow-md',
);

const TIPPY_DEFAULTS = {
  animateFill: true,
  plugins: [animateFill],
  duration: [200, 150] as [number, number],
  delay: [300, 0] as [number, number],
  arrow: false,
};

/** Hook que retorna um handler de click para interceptar rotas restritas para convidados */
function use_guest_nav_guard() {
  const { is_anonymous } = useAuth();
  const { open_conversion_dialog } = useGuestConversion();

  /** Retorna true se a navegacao foi bloqueada (convidado em rota restrita) */
  const guard = (item: NavItem, e: MouseEvent) => {
    if (is_anonymous && item.guest_restricted) {
      e.preventDefault();
      open_conversion_dialog();
      return true;
    }
    return false;
  };

  return guard;
}

/* ───── Sidebar Flutuante ───── */
function SidebarNav({ on_sign_out }: { on_sign_out: () => void }) {
  const location = useLocation();
  const guard = use_guest_nav_guard();
  const [source, target] = useSingleton({
    moveTransition: 'transform 0.2s ease-out',
    delay: [300, 0],
    arrow: false,
  } as Parameters<typeof useSingleton>[0]);
  const [collapsed, set_collapsed] = useState(false);

  return (
    <motion.aside
      animate={{ width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED }}
      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
      className="sticky top-0 h-screen shrink-0 z-30 overflow-hidden p-4 pr-0"
    >
      <div className={cn(FLOATING_PANEL, 'flex flex-col h-full overflow-hidden')}>
        {/* Cabecalho */}
        <div className="flex items-center h-14 px-4 justify-between">
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.div
                key="logo"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15 }}
              >
                <BrandLogo to="/" showTagline={false} />
              </motion.div>
            )}
          </AnimatePresence>
          <Tippy content={collapsed ? 'Expandir' : 'Recolher'} placement="right" {...TIPPY_DEFAULTS}>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-xl"
              onClick={() => set_collapsed(!collapsed)}
            >
              <motion.div
                animate={{ rotate: collapsed ? 180 : 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                {collapsed ? <List className="h-4 w-4" /> : <CaretLeft className="h-3.5 w-3.5" />}
              </motion.div>
            </Button>
          </Tippy>
        </div>

        {collapsed && <Tippy singleton={source} animateFill plugins={[animateFill]} placement="right" />}

        {/* Navegacao */}
        <nav className="flex-1 px-3 py-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.href;
            const link_el = (
              <Link
                to={item.href}
                onClick={(e) => guard(item, e)}
                className={cn(
                  'relative flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors overflow-hidden',
                  active
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                  collapsed && 'justify-center px-0',
                )}
              >
                {active && (
                  <motion.span
                    layoutId="sidebar-active"
                    className="absolute inset-0 rounded-2xl bg-primary"
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  />
                )}
                <item.icon className="h-[18px] w-[18px] shrink-0 relative z-10" weight={active ? 'fill' : 'regular'} />
                <AnimatePresence mode="wait">
                  {!collapsed && (
                    <motion.span
                      key="label"
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15 }}
                      className="relative z-10 whitespace-nowrap overflow-hidden"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            );

            if (collapsed) {
              return (
                <Tippy key={item.href} content={item.label} singleton={target}>
                  {link_el}
                </Tippy>
              );
            }
            return <div key={item.href}>{link_el}</div>;
          })}
        </nav>

        {/* Rodape */}
        <div className={cn(
          'px-3 py-3 space-y-1',
          collapsed && 'flex flex-col items-center',
        )}>
          <div className={cn('flex gap-0.5', collapsed ? 'flex-col items-center' : 'items-center')}>
            <FeedbackModal />
            <NotificationCenter />
            <ThemeToggle className="h-8 w-8 rounded-xl" />
          </div>
          <Tippy content="Sair" placement={collapsed ? 'right' : 'top'} {...TIPPY_DEFAULTS}>
            <Button
              variant="ghost"
              size={collapsed ? 'icon' : 'sm'}
              onClick={() => { void on_sign_out(); }}
              className={cn(
                'text-muted-foreground hover:text-destructive hover:bg-destructive/10 w-full rounded-xl',
                collapsed && 'h-8 w-8',
              )}
            >
              <SignOut className="h-4 w-4" />
              <AnimatePresence mode="wait">
                {!collapsed && (
                  <motion.span
                    key="sair"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.15 }}
                    className="ml-2 whitespace-nowrap overflow-hidden"
                  >
                    Sair
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </Tippy>
        </div>
      </div>
    </motion.aside>
  );
}

/* ───── Topbar Flutuante ───── */
function TopbarNav({ on_sign_out }: { on_sign_out: () => void }) {
  const location = useLocation();
  const guard = use_guest_nav_guard();
  return (
    <div className="sticky top-0 z-40 p-4 pb-0">
      <header className={cn(FLOATING_PANEL, 'mx-auto max-w-5xl px-3 sm:px-4 py-2 flex items-center justify-between')}>
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <BrandLogo to="/" showTagline={false} />
          <nav className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto scrollbar-none">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={(e) => guard(item, e)}
                  className={cn(
                    'relative flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-2xl text-sm font-medium transition-colors whitespace-nowrap shrink-0',
                    active
                      ? 'text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                  )}
                >
                  {active && (
                    <motion.span
                      layoutId="topbar-active"
                      className="absolute inset-0 rounded-2xl bg-primary"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                  <item.icon className="h-4 w-4 relative z-10" weight={active ? 'fill' : 'regular'} />
                  <span className="relative z-10 hidden sm:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <FeedbackModal />
          <NotificationCenter />
          <ThemeToggle className="h-8 w-8 rounded-xl" />
          <Tippy content="Sair" placement="bottom" {...TIPPY_DEFAULTS}>
            <Button variant="ghost" size="icon" onClick={() => { void on_sign_out(); }} className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8 rounded-xl">
              <SignOut className="h-4 w-4" />
            </Button>
          </Tippy>
        </div>
      </header>
    </div>
  );
}

/* ───── Bottom Bar Flutuante (largura expandida) ───── */
function BottomBarNav() {
  const location = useLocation();
  const guard = use_guest_nav_guard();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [more_open, set_more_open] = useState(false);
  const [notifications_open, set_notifications_open] = useState(false);
  const [feedback_open, set_feedback_open] = useState(false);

  const handle_sign_out = useCallback(async () => {
    await signOut();
    navigate('/auth');
  }, [signOut, navigate]);

  return (
    <>
      {/* Componentes renderizados fora do menu para nao desmontar ao fechar */}
      <NotificationCenter controlled_open={notifications_open} on_controlled_change={set_notifications_open} hidden_trigger />
      <FeedbackModal controlled_open={feedback_open} on_controlled_change={set_feedback_open} hidden_trigger />

      <div className="fixed bottom-4 inset-x-4 z-40">
        <nav className={cn(FLOATING_PANEL, 'max-w-xl mx-auto px-3 py-2')}>
          <div className="flex items-center justify-evenly">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={(e) => guard(item, e)}
                  className={cn(
                    'relative flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-2xl transition-colors',
                    active
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <item.icon className="h-5 w-5" weight={active ? 'fill' : 'regular'} />
                  <span className="text-[10px] font-medium leading-none">{item.label}</span>
                </Link>
              );
            })}

            {/* Botao "Mais" com Logout, Notificacoes, Feedback */}
            <div className="relative">
              <button
                onClick={() => set_more_open((p) => !p)}
                className={cn(
                  'flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-2xl transition-colors',
                  more_open ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <DotsThree className="h-5 w-5" weight="bold" />
                <span className="text-[10px] font-medium leading-none">Mais</span>
              </button>

              <AnimatePresence>
                {more_open && (
                  <>
                    {/* Backdrop */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40"
                      onClick={() => set_more_open(false)}
                    />
                    {/* Menu popover */}
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      className={cn(
                        FLOATING_PANEL,
                        'absolute bottom-full right-0 mb-2 z-50 min-w-[180px] p-2 space-y-1',
                      )}
                    >
                      <button
                        onClick={() => { set_more_open(false); set_notifications_open(true); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
                      >
                        <Bell className="h-4 w-4" />
                        Notificações
                      </button>
                      <button
                        onClick={() => { set_more_open(false); set_feedback_open(true); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
                      >
                        <ChatCircle className="h-4 w-4" />
                        Feedback
                      </button>
                      <div
                        onClick={(e) => {
                          const btn = (e.currentTarget as HTMLElement).querySelector('button');
                          if (btn && e.target !== btn && !btn.contains(e.target as Node)) btn.click();
                        }}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors cursor-pointer"
                      >
                        <ThemeToggle className="h-8 w-8 rounded-xl" />
                        <span className="pointer-events-none">Tema</span>
                      </div>
                      <div className="h-px bg-border/40 my-1" />
                      <button
                        onClick={() => { set_more_open(false); void handle_sign_out(); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <SignOut className="h-4 w-4" />
                        Sair
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}

export { SidebarNav, TopbarNav, BottomBarNav };
